import {DuplicateWorkingTimeRequestEvent, EditWorkingTimeRequestEvent, ShowWorkingTimeRequestEvent, WorkingTimeTimelineDayContent} from "../timelineTypes";
import {EVENT_WORKING_TIME_DELETED, EVENT_WORKING_TIME_REQUEST_DUPLICATE, EVENT_WORKING_TIME_REQUEST_EDIT, EVENT_WORKING_TIME_REQUEST_SHOW} from "../timelineConstants";
import {htmlEscape} from "@/shared/utils/string";
import {formatDurationDays, formatHalfDay} from "@/shared/utils/datefunctions";
import {WorkingTimeDateSpanRequestResponseModel} from "@/shared/timr";
import {TimelineContextMenu} from "../timelineContextMenu";
import {buildContextMenu} from "../timelineHelper";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {WorkingTimeRequestStatus} from "@/shared/timrTypes";

/**
 * Component to display holiday information in a timeline day.
 * This component is used inside <code>TimelineDay</code> to render requests information for a specific day. If available, this component is appended to the day container.
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 *
 */
export class CombinedTimelineDayRequests extends HTMLElement {
	private _timelineDay: WorkingTimeTimelineDayContent;

	set timelineDay(value: WorkingTimeTimelineDayContent) {
		this._timelineDay = value;
	}

	/**
	 * Main entry point to render the requests information.
	 */
	public connectedCallback(): void {
		// timeline day (content) and working time requests are required to render this component
		if (!this._timelineDay || !this._timelineDay.workingTimeRequests.length) {
			return;
		}
		const items = this._timelineDay.workingTimeRequests
			.map(request => {
				const showHalfDay = request.startHalfDay && this._timelineDay.date === request.start || request.endHalfDay && this._timelineDay.date === request.end;

				const escapedWorkingTimeTypeName = htmlEscape(request.workingTimeType.name);
				const escapedRequestDescription = htmlEscape(request.description);

				const div = document.createElement("div");
				div.dataset.toggle = "context";
				div.classList.add("working-time-request");
				div.innerHTML = `
					<!-- timestamps and circles -->
					<span class="times">
						<span>${formatDurationDays(showHalfDay ? 0.5 : 1)} ${messages["report.unit.day"]}</span>
						<span></span>
					</span>
					<div class="panel panel-requests border-color border-${request.workingTimeType.category} pb-15">
						<!-- working time -->
						<div class="panel-heading">
							<div>
								<div class="item-working-time-type">
									<span class="label request-status-${request.status} request-category-${request.workingTimeType.category} mr-5">
										${messages["workingTimeRequestStatus." + request.status]}
									</span>
									<span>${escapedWorkingTimeTypeName}</span>
									<span>${showHalfDay ? formatHalfDay(true) : ""}</span>
								</div>
								<div class="duration-container">
									<div class="display-flex">
										${formatDurationDays(showHalfDay ? 0.5 : 1)} ${messages["report.unit.day"]}
									</div>
									<div class="info-icons-container"></div>
								</div>
								${buildContextMenu().outerHTML}
							</div>
							<div class="description-container" style="${!escapedRequestDescription ? "display: none" : ""} ">
								<i class="fal fa-comment-alt position-left"></i>
								<span data-description class="description-clamped">${escapedRequestDescription}</span>
							</div>
						</div>
					</div>`;

				this.buildContextMenuForItem(div, request);

				div.addEventListener("click", (e) => {
					if (!(e.target instanceof HTMLElement) || e.target.closest(".dropdown-menu")) {
						return;
					}
					this.onRequestClicked(request);
				});
				return div;
			});

		this.append(...items);
	}

	private onRequestClicked(workingTimeRequest: WorkingTimeDateSpanRequestResponseModel): void {
		const eventName = workingTimeRequest.status === WorkingTimeRequestStatus.ApprovalRequested
			? EVENT_WORKING_TIME_REQUEST_EDIT
			: EVENT_WORKING_TIME_REQUEST_SHOW;
		this.dispatchEvent(new CustomEvent<ShowWorkingTimeRequestEvent | EditWorkingTimeRequestEvent>(
			eventName, {
				detail: {workingTimeRequest},
				bubbles: true
			}
		));
	}

	private buildContextMenuForItem(div: HTMLElement, workingTimeRequest: WorkingTimeDateSpanRequestResponseModel): void {
		new TimelineContextMenu<WorkingTimeDateSpanRequestResponseModel>()
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.show"],
				action: request => this.dispatchEvent(new CustomEvent<ShowWorkingTimeRequestEvent>(
					EVENT_WORKING_TIME_REQUEST_SHOW, {
						detail: {workingTimeRequest: request},
						bubbles: true
					}
				))
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.edit"],
				action: request => this.dispatchEvent(new CustomEvent<EditWorkingTimeRequestEvent>(
					EVENT_WORKING_TIME_REQUEST_EDIT, {
						detail: {workingTimeRequest: request},
						bubbles: true
					}
				)),
				active: request => request.status === WorkingTimeRequestStatus.ApprovalRequested
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.duplicate"],
				action: request => this.dispatchEvent(new CustomEvent<DuplicateWorkingTimeRequestEvent>(
					EVENT_WORKING_TIME_REQUEST_DUPLICATE, {
						detail: {workingTimeRequest: request},
						bubbles: true
					}
				))
			})
			.addMenuEntry({
				label: messages["requests.workingTimeRequestsUser.context.delete"],
				action: request => this.deleteWorkingTimeRequest(request),
				active: request => WorkingTimeRequestUtil.isInApprovalProcess(request),
			})
			.bindToElement(div, workingTimeRequest);
	}

	private deleteWorkingTimeRequest(workingTimeRequest: WorkingTimeDateSpanRequestResponseModel): void {
		WorkingTimeRequestUtil.deleteWorkingTimeRequest(workingTimeRequest,
			() => this.dispatchEvent(new CustomEvent(EVENT_WORKING_TIME_DELETED, {bubbles: true})));
	}
}
